import React, { useState } from 'react';
import { connect } from 'react-redux';
import ReactHtmlParser from '@orrisroot/react-html-parser';
import { GetLatestPolicyInfo, SubmitAutoRenewFeedback, UpdateAutorenewal } from 'services/MyRacService';
import { ResponseCode } from 'services/models/httpResponse';
import { setSelectedAutoRenew } from 'actions/policyInfoActions';
import useCosmosTrackEvent from 'hooks/useCosmosTrackEvent';
import './AutorenewalPanel.scss';
import trackEvent from '../../../../services/AnalyticsService';
import ProtectedForm from '../../../../shared/components/ProtectedForm/ProtectedForm';
import isInRenewalPeriod from '../../../../utils/autoRenewalUtils';
import AutoRenewOptOutJourney from './AutoRenewOptOutJourney/AutoRenewOptOutJourney';
import AutoRenewInfoModal from './AutoRenewOptOutJourney/AutoRenewInfoModal';

export const AutorenewalPanel = (props) => {
  const { pageModel, dispatch, policy: { policyInfo } } = props;
  const [showDocPref, setShowDocPref] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [autoRenew, setAutoRenew] = useState(policyInfo.AutoRenew.IsAutoRenew);
  const [autoRenewInitialState, setAutoRenewInitialState] = useState(policyInfo.AutoRenew.IsAutoRenew);
  const [infoModal, setInfoModal] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showOptOutModal, setShowOptOutModal] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [isInRenewalPeriodResult] = useState(isInRenewalPeriod(policyInfo.DaysOfCoverLeft));

  const { cosmosTrackEvent } = useCosmosTrackEvent('AccountSettings');

  const messages = pageModel?.messages;

  // Callers deliberately pass no key where a journey step has no legacy tag.
  const trackTag = (key) => {
    if (!key) {
      return;
    }

    const tag = pageModel?.tagging?.find((item) => item.key === key);
    if (tag?.text) {
      trackEvent(tag.text);
    }
  };

  const openInfoModal = (data) => {
    setInfoModal(data);
    setShowInfoModal(true);
  };

  const showDialogue = (dialogue) => openInfoModal({
    success: false,
    autoRenew,
    title: dialogue?.title,
    message: dialogue?.body,
  });

  const showResult = (success) => {
    cosmosTrackEvent(`AccountSettings_AutoRenew_${autoRenew ? 'OptIn' : 'OptOut'}_Result_Displayed`, { success });

    openInfoModal({
      success,
      autoRenew,
      title: success ? messages?.successTitle : messages?.failTitle,
      message: success ? messages?.successMessage : messages?.failMessage,
    });
  };

  const closeInfoModal = () => {
    setShowInfoModal(false);
    setWaitingForResponse(false);
  };

  const toggleShowDocPref = (e) => {
    e.preventDefault();
    if (autoRenewInitialState === autoRenew) {
      setShowDocPref(!showDocPref);
      setSubmitDisabled(!submitDisabled);
      cosmosTrackEvent('AccountSettings_AutoRenew_Change_Preferences');
    }
  };

  const optOut = () => {
    trackTag('AutoRenewOptOut');
    setAutoRenew(false);
  };

  const optIn = () => {
    trackTag('AutoRenewOptIn');
    setAutoRenew(true);
  };

  const blockChange = (alreadySelected, tags) => {
    if (policyInfo.AutoRenew.IsAutoRenewChangeEligible || !alreadySelected) {
      return;
    }

    trackTag(tags.notAllowed);

    if (isInRenewalPeriodResult) {
      trackTag(tags.inRenewalNotAllowed);
      showDialogue(messages?.RenewalInProgressDialogue);
    } else {
      showDialogue(messages?.optInDialogue);
    }
  };

  const blockOptIn = () => blockChange(!autoRenew && !autoRenewInitialState, {
    notAllowed: 'AutoRenewOptInNotAllowed',
    inRenewalNotAllowed: 'AutoRenewOptInInRenewalNotAllowed',
  });

  const blockOptOut = () => blockChange(autoRenew && autoRenewInitialState, {
    inRenewalNotAllowed: 'AutoRenewOptOutInRenewalNotAllowed',
  });

  /**
   * The preference on its own - no feedback rides along. Returns whether it
   * saved so the opt-out journey knows whether it may move on to the survey.
   */
  const saveAutoRenewPreference = async () => {
    const direction = autoRenew ? 'OptIn' : 'OptOut';

    try {
      const response = await UpdateAutorenewal({
        isAutoRenew: autoRenew,
        policyId: policyInfo?.PolicyId,
      });

      if (response.responseCode !== ResponseCode.SUCCESS) {
        throw new Error(`Autorenewal update returned ${response.responseCode}`);
      }

      trackTag(`AutoRenew${direction}SubmitConfirm`);
      cosmosTrackEvent(`AccountSettings_AutoRenew_${direction}_Confirm`);

      /**
       * The refetch replaces the whole policy slice, and the API can still be
       * serving the pre-update preference, so the confirmed choice is written
       * back afterwards. The policy slice is persisted to sessionStorage, which
       * is what the panel reads after a page refresh.
       */
      try {
        await GetLatestPolicyInfo();
      } catch {
        // Nothing else depends on the refetch here - the update itself succeeded.
      }

      dispatch(setSelectedAutoRenew(autoRenew));
      setAutoRenewInitialState(autoRenew);
      return true;
    } catch {
      setAutoRenew(autoRenewInitialState);
      trackTag(`AutoRenew${direction}Error`);
      cosmosTrackEvent(`AccountSettings_AutoRenew_${direction}_Error`);
      return false;
    }
  };

  // Opting back in has no journey, so its result modal follows the call directly.
  const confirmAutoRenewal = async () => {
    setWaitingForResponse(true);
    showResult(await saveAutoRenewPreference());
  };

  /**
   * Saves the opt-out itself. The journey stays open either way: on success it
   * moves on to the survey, on failure the failure modal covers it from above.
   */
  const confirmOptOut = async () => {
    const saved = await saveAutoRenewPreference();

    if (!saved) {
      setShowOptOutModal(false);
      showResult(false);
    }

    return saved;
  };

  /**
   * Sent on its own, after the opt-out is already saved. The opt-out itself
   * stays safe either way. An answered survey earns a retry on failure, but a
   * skip already told the member nothing was captured, so its failure stays
   * invisible to them - only tracked, never surfaced.
   */
  const submitFeedback = async (feedback) => {
    try {
      await SubmitAutoRenewFeedback(feedback);
    } catch {
      cosmosTrackEvent('AccountSettings_AutoRenew_OptOut_Feedback_Error');
      if (!feedback.skipped) {
        return false;
      }
    }

    setShowOptOutModal(false);
    // The survey already confirmed the opt-out, so this modal is the sign-off.
    cosmosTrackEvent('AccountSettings_AutoRenew_OptOut_Confirmation_Displayed');
    openInfoModal({
      success: true,
      autoRenew: false,
      title: messages?.successTitle,
      message: messages?.successMessage,
    });

    return true;
  };

  const cancelOptOut = (stage) => {
    /**
     * Only the "No thanks" step maps to the legacy tag - it was the sole exit
     * before the survey was added, so tagging the survey's dismissal too would
     * inflate it. The survey's own dismissal gets its own cosmos event instead.
     */
    if (stage === 'confirm') {
      trackTag('AutoRenewOptOutSubmitCancel');
    } else {
      cosmosTrackEvent('AccountSettings_AutoRenew_OptOut_Feedback_Cancel');
    }

    setShowOptOutModal(false);

    /**
     * Abandoning the survey leaves the opt-out saved - it went in before the
     * survey was ever shown - so only the confirmation step rewinds the toggle.
     */
    if (stage === 'confirm') {
      setAutoRenew(autoRenewInitialState);
    }
  };

  const submitAutoRenewalPreferences = (e) => {
    e.preventDefault();

    cosmosTrackEvent('AccountSettings_AutoRenew_Change_Preferences_Submit');
    trackTag(autoRenew ? 'AutoRenewOptInSubmit' : 'AutoRenewOptOutSubmit');

    if (!autoRenew && (autoRenewInitialState !== autoRenew) && policyInfo.AutoRenew.IsAutoRenewChangeEligible) {
      // The only way the journey opens, so this is where its first modal is seen.
      cosmosTrackEvent('AccountSettings_AutoRenew_OptOut_Warning_Displayed');
      setShowOptOutModal(true);
    } else {
      confirmAutoRenewal();
    }
  };

  return (
    <div className="row border pt-4 pb-3 mt-3">
      <div className="account-preferences__icon col-sm-1 col-1">
        <img src={pageModel?.image.url} alt="Email" />
      </div>
      <div className="col-sm-11 col-11">
        <h3 className="account-preferences__prefs-title">{pageModel?.title}</h3>
        <p className="action edit">
          <button
            type="button"
            className="MyRacForm__button-link link Panel__button"
            onClick={toggleShowDocPref}
          >
            {pageModel?.button}
            {' '}
            &gt;
          </button>
        </p>
        {showDocPref
          && (
            <div>
              <ProtectedForm formChanged={autoRenewInitialState !== autoRenew}>
                <div>{ReactHtmlParser(pageModel?.text)}</div>
                {isInRenewalPeriodResult ? <p className="warning">{ReactHtmlParser(pageModel?.optOutDisabledMessage)}</p> : null}
                <div className="myrac-doc__efulfilment--wrapper">
                  <div className="myrac__checkbox--wrapper">
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
                    <label
                      className="myrac__checkbox myrac__checkbox--label cursor__pointer"
                      htmlFor="opt-in"
                      onClick={blockOptIn}
                    >
                      <input
                        id="opt-in"
                        className="myrac__checkbox--input autorenew"
                        type="radio"
                        name="autorenew"
                        checked={autoRenew}
                        disabled={!policyInfo.AutoRenew.IsAutoRenewChangeEligible || isInRenewalPeriodResult}
                        onChange={optIn}
                        onClick={blockOptIn}
                      />
                      <span className="myrac__checkbox--span" />
                      <span
                        className="myrac__checkbox--text checkbox--verticalAlign"
                      >
                        {pageModel?.options.optIn}
                      </span>
                    </label>
                  </div>
                  <div className="myrac__checkbox--wrapper">
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
                    <label
                      className="myrac__checkbox--text myrac__checkbox--label autorenew"
                      htmlFor="opt-out"
                      onClick={blockOptOut}
                    >
                      <input
                        id="opt-out"
                        className="myrac__checkbox--input autorenew"
                        type="radio"
                        name="autorenew"
                        checked={!autoRenew}
                        disabled={!policyInfo.AutoRenew.IsAutoRenewChangeEligible || isInRenewalPeriodResult}
                        onClick={blockOptOut}
                        onChange={optOut}
                      />
                      <span className="myrac__checkbox--span" />
                      <span
                        className="myrac__checkbox--text checkbox--verticalAlign"
                      >
                        {pageModel?.options.optOut}
                      </span>
                    </label>
                  </div>
                </div>
                <div className="account-preferences__button-wrapper">
                  <button
                    type="button"
                    onClick={submitAutoRenewalPreferences}
                    className="rac-btn btn-cta btn-primary"
                    data-dismiss="modal"
                    disabled={submitDisabled || autoRenewInitialState === autoRenew || waitingForResponse}
                  >
                    {waitingForResponse
                      ? <i className="fas fa-spinner fa-pulse" />
                      : pageModel?.submit}
                  </button>
                </div>
              </ProtectedForm>
            </div>
          )}

        <AutoRenewOptOutJourney
          show={showOptOutModal}
          onConfirm={confirmOptOut}
          onSubmitFeedback={submitFeedback}
          onCancel={cancelOptOut}
          policyEnd={policyInfo?.CoverEnd}
        />

        {infoModal && (
          <AutoRenewInfoModal
            show={showInfoModal}
            data={infoModal}
            onConfirm={closeInfoModal}
            onExited={() => setInfoModal(null)}
            buttonText={pageModel.modalOk}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  policy: state.policy,
});

export default connect(mapStateToProps)(AutorenewalPanel);
